import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Trash2, Download, MapPin, Tag, Upload } from "lucide-react";
import type { KBDocument } from "@/lib/knowledge-base-service";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface DocumentGridProps {
  documents: KBDocument[];
  loading: boolean;
  onDelete: (doc: KBDocument) => Promise<void>;
  onDownload: (doc: KBDocument) => Promise<void>;
  onUploadClick: () => void;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DocumentCardProps {
  doc: KBDocument;
  onPreview: (doc: KBDocument) => void;
  onDelete: (doc: KBDocument) => void;
  onDownload: (doc: KBDocument) => void;
}

function DocumentCard({ doc, onPreview, onDelete, onDownload }: DocumentCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canPreview = doc.status === 'ready' || doc.status === 'failed';

  return (
    <>
      <div
        className={cn(
          "group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-all",
          canPreview && "cursor-pointer hover:shadow-md hover:border-primary/30"
        )}
        onClick={() => canPreview && onPreview(doc)}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight truncate" title={doc.name}>
              {doc.name}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}
              {doc.file_size ? ` · ${formatSize(doc.file_size)}` : ""}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5">
          <DocumentStatusBadge status={doc.status} />
          {doc.jurisdiction && (
            <Badge variant="secondary" className="gap-1 text-xs">
              <MapPin className="h-2.5 w-2.5" />
              {doc.jurisdiction}
            </Badge>
          )}
          {doc.regulation_type && (
            <Badge variant="outline" className="gap-1 text-xs">
              <Tag className="h-2.5 w-2.5" />
              {doc.regulation_type}
            </Badge>
          )}
        </div>

        {/* Actions — stop propagation so card click doesn't fire when hitting buttons */}
        <div
          className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={e => e.stopPropagation()}
        >
          {doc.file_path && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => onDownload(doc)}
            >
              <Download className="h-3 w-3" />
              Download
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{doc.name}</strong> will be permanently removed along with its indexed content. The AI agent will no longer draw from it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onDelete(doc)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function DocumentGrid({
  documents,
  loading,
  onDelete,
  onDownload,
  onUploadClick,
}: DocumentGridProps) {
  const [previewDoc, setPreviewDoc] = useState<KBDocument | null>(null);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-36 rounded-xl border border-border bg-muted/30 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 mb-4">
          <Upload className="h-6 w-6 text-primary" />
        </div>
        <p className="text-sm font-medium text-foreground">No documents yet</p>
        <p className="text-xs text-muted-foreground mt-1 mb-4 max-w-xs">
          Upload regulation PDFs and compliance documents. The AI agent will draw from them when writing policies.
        </p>
        <Button size="sm" onClick={onUploadClick}>
          Upload document
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map(doc => (
          <DocumentCard
            key={doc.id}
            doc={doc}
            onPreview={setPreviewDoc}
            onDelete={onDelete}
            onDownload={onDownload}
          />
        ))}
      </div>

      <DocumentPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={async doc => {
          await onDownload(doc);
        }}
      />
    </>
  );
}
